/**
 * useBoardZoomToPoint - pinch-to-zoom with cursor-stable scroll correction
 *
 * Uses refs instead of closure values to avoid stale-closure flickering:
 * multiple wheel events can fire between React re-renders, and each must
 * read the latest zoom level (updated immediately via ref).
 */

import { useRef, useEffect, useLayoutEffect } from 'react';

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 20.0;
const ZOOM_SENSITIVITY = 0.008;

export function useBoardZoomToPoint(
  containerRef: React.RefObject<HTMLDivElement | null>,
  zoomLevel: number,
  columnLabelWidth: number,
  onZoomChange?: (zoomLevel: number) => void,
) {
  const pendingScrollRef = useRef<{ scrollLeft: number } | null>(null);
  const zoomLevelRef = useRef(zoomLevel);
  const onZoomChangeRef = useRef(onZoomChange);
  const columnLabelWidthRef = useRef(columnLabelWidth);

  // Keep refs in sync
  zoomLevelRef.current = zoomLevel;
  onZoomChangeRef.current = onZoomChange;
  columnLabelWidthRef.current = columnLabelWidth;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const onZoom = onZoomChangeRef.current;
        if (!onZoom) return;

        const currentZoom = zoomLevelRef.current;
        const delta = -e.deltaY * ZOOM_SENSITIVITY;
        const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, currentZoom * (1 + delta)));

        // Zoom-to-cursor: keep the content point under the cursor stable
        const rect = container.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const clw = columnLabelWidthRef.current;
        const ratio = newZoom / currentZoom;

        // Use pending scroll if multiple events fire in same frame
        const currentScrollLeft = pendingScrollRef.current?.scrollLeft ?? container.scrollLeft;
        const contentX = currentScrollLeft + cursorX;
        const timelineX = Math.max(0, contentX - clw);
        const newTimelineX = timelineX * ratio;
        const newScrollLeft = Math.max(0, clw + newTimelineX - cursorX);

        pendingScrollRef.current = { scrollLeft: newScrollLeft };
        zoomLevelRef.current = newZoom; // update immediately for next event in same frame
        onZoom(newZoom);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []); // stable - all values read from refs

  // Apply scroll correction synchronously after zoom re-render (before browser paint)
  useLayoutEffect(() => {
    if (pendingScrollRef.current && containerRef.current) {
      containerRef.current.scrollLeft = pendingScrollRef.current.scrollLeft;
      pendingScrollRef.current = null;
    }
  });
}
